// Hallticket


import React, { memo, useEffect, useState } from 'react';
import StyleComponentProps from 'core/utils/types/StyleComponentProps';
import moment from 'moment';
import 'moment/locale/en-au';
import { SetupLoader } from 'core/components/SetupComponents/SetupLoader/Loadable';
import styles from './print-styles.module.css'
import { useAuth } from 'core/context/AuthContext/AuthContextProvider';
import Avatar from "assets/icons/Avatar.svg";
import { convertNaNIntoNumber } from 'core/utils/constants';
import { calculateSizeScale } from 'core/utils/constants/calculateSizeScale';
import WithPerPage from './WithPerPage';
import GoogleTranslate from 'core/components/GoogleTranslate';
import EncryptedStorage from 'core/utils/constants/encryptedStorage';
import { Info } from 'lucide-react';

interface Props extends StyleComponentProps { }

const initialRangeValues = JSON.parse(EncryptedStorage.getItem('htRangeValues') as string) || {
  logo: 4,
  mainHeading: 4,
  subHeading: 4,
  text: 4,
};


export const HallticketGenerator = memo((props: Props) => {
  const authData = useAuth();

  const [schoolNameAlignment, setschoolNameAlignment] = React.useState('default');
  const [examNameAlignment, setexamNameAlignment] = React.useState('left');

  const [space, setSpace] = React.useState(20);
  const cardRef = React.useRef<HTMLDivElement>(null);


  const [sizeScale, setSizeScale] = React.useState(10);
  const [perPage, setPerPage] = React.useState(2);
  const [perPageToggle, setPerPageToggle] = React.useState(false);
  const [hasSubjectType, setHasSubjectType] = React.useState(false);

  const [selectedOption, setSelectedOption] = useState(
    EncryptedStorage.getItem('htSelectedOption') || 'logo'
  );
  const [rangeValues, setRangeValues] = useState(initialRangeValues);

  const [cardHeight, setCardHeight] = useState(0);
  const gapPx = 5;
  const pageHeightPx = 1050;

  const handleSelectChange = (event) => {
    setSelectedOption(event.target.value);
  };

  const handleRangeChange = (event) => {
    const value = event.target.value;
    setRangeValues({
      ...rangeValues,
      [selectedOption]: Number(value),
    });
  };

  useEffect(() => {
    EncryptedStorage.setItem('htRangeValues', JSON.stringify(rangeValues));
  }, [rangeValues]);

  useEffect(() => {
    EncryptedStorage.setItem('htSelectedOption', selectedOption);
  }, [selectedOption]);

  useEffect(() => {
    const totalGapHeight = (perPage - 1) * gapPx;
    const calculatedCardHeight = (pageHeightPx - totalGapHeight) / perPage;
    setCardHeight(calculatedCardHeight);
  }, [perPage, gapPx]);


  const handlePerPage = (event) => {
    setPerPage(event.target.value);
  };

  const handlePerPageToggle = (e) => {
    setPerPageToggle(e.target.checked);
  };


  const handlePrintClick = () => {
    window.print();
  };


  React.useEffect(() => {
    const schoolNameAlignment = EncryptedStorage.getItem('HALL_TICKET_SCHOOL_NAME_ALIGNMENT');
    const examNameAlignment = EncryptedStorage.getItem('HALL_TICKET_EXAM_NAME_ALIGNMENT');
    const subjectType = EncryptedStorage.getItem('HALL_TICKET_SUBJECT_TYPE');
    if (schoolNameAlignment) {
      setschoolNameAlignment(schoolNameAlignment);
    }
    if (examNameAlignment) {
      setexamNameAlignment(examNameAlignment);
    }
    if (subjectType !== null) {
      setHasSubjectType(JSON.parse(subjectType));
    }
  }, [])


  const studentsDataString = EncryptedStorage.getItem("filterHallticketResults");
  const studentsData = studentsDataString ? JSON.parse(studentsDataString) : null;
  const sortedSubjects = studentsData?.ExamSubjects;
  // const sortedSubjects = studentsData?.ExamSubjects?.sort((a, b) => {
  //   const dateA: moment.Moment = moment(a.ExamStartDatetime);
  //   const dateB: moment.Moment = moment(b.ExamStartDatetime);

  //   if (dateA.isSame(dateB)) {
  //     const timeA = moment(a.ExamStartDatetime).format('HH:mm');
  //     const timeB = moment(b.ExamStartDatetime).format('HH:mm');
  //     return timeA.localeCompare(timeB);
  //   }

  //   return dateA.isBefore(dateB) ? -1 : 1;
  // });

  useEffect(() => {
    const sizeScale = calculateSizeScale(perPage, Number(sortedSubjects?.length));
    setSizeScale(sizeScale);
  }, [perPage, sortedSubjects?.length])


  const saveSchoolNameAlignment = (state) => {
    EncryptedStorage.setItem('HALL_TICKET_SCHOOL_NAME_ALIGNMENT', state);
  };
  const saveExamNameAlignment = (state) => {
    EncryptedStorage.setItem('HALL_TICKET_EXAM_NAME_ALIGNMENT', state);
  };

  const saveSubjectType = (state) => {
    EncryptedStorage.setItem('HALL_TICKET_SUBJECT_TYPE', state)
  }


  const handleSchoolNameAlignment = (event: React.ChangeEvent<HTMLInputElement>) => {
    setschoolNameAlignment(event.target.value);
    saveSchoolNameAlignment(event.target.value);
  };

  const handleExamNameAlignment = (event: React.ChangeEvent<HTMLInputElement>) => {
    setexamNameAlignment(event.target.value);
    saveExamNameAlignment(event.target.value);
  }

  const handleSubjectTypeChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setHasSubjectType(event.target.checked);
    saveSubjectType(event.target.checked);
  };




  React.useEffect(() => {
    const subjectsLength = sortedSubjects?.length

    if (subjectsLength >= 7) {
      setSpace(180);
    } else if (subjectsLength >= 5) {
      setSpace(210)
    } else if (subjectsLength >= 3) {
      setSpace(80);
    } else {
      setSpace(80);
    }
  }, [handlePrintClick, sortedSubjects, studentsData]);


  return Boolean(!!studentsData && !!authData) ? (
    <div className="w-[95%] mx-auto">
      <div className='mt-2 space-y-2 print:space-y-0'>
        <div className="flex justify-center items-center print:hidden py-3">
          <div className='flex gap-5 items-center'>
            <div className='flex gap-2'>
              <p className='font-semibold text-md'>School Name:</p>
              <div className='flex items-center '>
                <input
                  type="radio"
                  id='default'
                  value={'default'}
                  checked={schoolNameAlignment === 'default'}
                  onChange={handleSchoolNameAlignment}
                  className='mr-2'
                />
                <label htmlFor='default'>Default</label>
              </div>
              <div className='flex items-center '>
                <input
                  type="radio"
                  id='style1'
                  value={'style1'}
                  checked={schoolNameAlignment === 'style1'}
                  onChange={handleSchoolNameAlignment}
                  className='mr-2'
                />
                <label htmlFor='style1'>Style 1</label>
              </div>
              <div className='flex items-center '>
                <input
                  type="radio"
                  id='style2'
                  value={'style2'}
                  checked={schoolNameAlignment === 'style2'}
                  onChange={handleSchoolNameAlignment}
                  className='mr-2'
                />
                <label htmlFor='style2'>Style 2</label>
              </div>
            </div>

            <div className='w-[2px] h-6 bg-slate-600'></div>
            <div className='flex gap-2'>
              <p className='font-semibold text-md'>Exam Name:</p>
              <div className='flex items-center '>
                <input
                  type="radio"
                  id='left'
                  value={'left'}
                  name='schoolName'
                  checked={examNameAlignment === 'left'}
                  onChange={handleExamNameAlignment}
                  className='mr-2'
                />
                <label htmlFor='left'>Left</label>
              </div>
              <div className='flex items-center '>
                <input
                  type="radio"
                  id='center'
                  value={'center'}
                  name='schoolName'
                  checked={examNameAlignment === 'center'}
                  onChange={handleExamNameAlignment}
                  className='mr-2'
                />
                <label htmlFor='center'>Center</label>
              </div>
            </div>

            <div className='w-[2px] h-6 bg-slate-600'></div>
            {!perPageToggle && (
              <>
                <div className='flex items-center gap-2'>
                  <select value={selectedOption} onChange={handleSelectChange} className="w-[8vw] p-2 bg-white shadow-md rounded-md text-black font-semibold">
                    <option value="logo">Logo</option>
                    <option value="mainHeading">Main Heading</option>
                    <option value="subHeading">Sub Heading</option>
                    <option value="text">Text</option>
                  </select>

                  <div className='relative'>
                    <input
                      id="rangeInput"
                      type="range"
                      min="0"
                      max="10"
                      value={rangeValues[selectedOption]}
                      onChange={handleRangeChange}
                      className='w-full'
                    />
                    <span
                      className='absolute text-center'
                      style={{
                        left: `${(rangeValues[selectedOption] + 0.3) * 10}%`,
                        transform: 'translateX(-50%)',
                        top: '16px',
                      }}
                    >
                      {`${rangeValues[selectedOption] === 0 ? '0%' : `${rangeValues[selectedOption]}0%`}`}</span>
                  </div>
                  <div className="w-[40%]">
                    <GoogleTranslate />
                  </div>
                </div>
              </>
            )}
            <div className="flex items-center gap-1">
              <input
                type="checkbox"
                className="toggle"
                id="perPageToggle"
                name="PER_PAGE_TOGGLE"
                checked={perPageToggle}
                onChange={handlePerPageToggle}
              />
              <label htmlFor="perPageToggle" className="label-text font-normal  cursor-pointer">
                Per page
              </label>
            </div>

            {perPageToggle && (
              <div>
                <select
                  id="per-page"
                  value={perPage}
                  onChange={handlePerPage}
                  className="border p-1 rounded w-16">
                  <option value={1}>1</option>
                  <option value={2}>2</option>
                  <option value={3}>3</option>
                </select>
              </div>
            )}
          </div>

          <div className='w-[2px] h-6 bg-slate-600 mx-4'></div>


          <div className="flex items-center gap-1">
            <input
              type="checkbox"
              className="toggle"
              id="subjectType"
              name="SUBJECT_TYPE"
              checked={hasSubjectType}
              onChange={handleSubjectTypeChange}
            />
            <label htmlFor="subjectType" className="label-text font-normal cursor-pointer">
              Subject Type
            </label>
          </div>

          <button
            className="bg-blue-500 text-white py-2 px-4 ml-4 rounded  hover:bg-blue-600"
            onClick={handlePrintClick}

          >
            Print
          </button>
        </div>

        {perPageToggle ? (
          <WithPerPage
            studentsData={studentsData}
            authData={authData}
            sizeScale={sizeScale}
            schoolNameAlignment={schoolNameAlignment}
            examNameAlignment={examNameAlignment}
            sortedSubjects={sortedSubjects}
            cardHeight={cardHeight}
            perPage={perPage}
            hasSubjectType={hasSubjectType}
          />
        ) : (
          <div className={`${styles.hallTickets} w-full grid grid-cols-1 print:space-y-0 space-y-8`}>
            {studentsData?.Students?.map((object, objectIndex) => (
              <div className={`${styles.card} ${styles.print}`} key={objectIndex}>
                <div style={{ height: `${space}px` }} className="print:block hidden w-full"></div>
                <div ref={cardRef} className="p-2 border border-black bg-white rounded-lg bg-[ #EBEBEB]">
                  {studentsData?.HallTicketSettings?.SHOW_HEADER && (
                    <div className={`pb-2 flex items-center ${schoolNameAlignment === 'style1' && `justify-between`} ${schoolNameAlignment === 'style2' && `justify-center`}`}>
                      {rangeValues?.logo !== 0 && studentsData?.HallTicketSettings?.SHOW_HEADER_SCHOOL_LOGO && (
                        <div className='ml-8 flex-shrink-0'>
                          <img
                            src={authData?.BRANCH_LOGO || Avatar}
                            alt="school-logo"
                            style={{
                              width: `${rangeValues.logo * 12}px`,
                              height: `${rangeValues.logo * 12}px`,
                            }}
                            className="rcLogo object-contain rounded-full"
                          />
                        </div>
                      )}

                      <div
                        className={`flex flex-col ${schoolNameAlignment !== `style2` && `flex-1`} ${schoolNameAlignment === 'style1' && `text-center items-center`}`}
                        style={{
                          padding: '0 10px',
                          maxWidth: '100%',
                          overflowX: 'hidden',
                        }}
                      >
                        {studentsData?.HallTicketSettings?.SHOW_HEADER_SCHOOL_NAME && (
                          <h1
                            className={`rcMainHeading font-bold text-black ${schoolNameAlignment === `style2` && `text-center`}`}
                            style={{
                              fontSize: `${rangeValues.mainHeading + 17}px`,
                              wordWrap: 'break-word',
                              overflowWrap: 'break-word',
                            }}
                          >
                            {authData?.BRANCH_NAME?.toUpperCase()}
                          </h1>
                        )}

                        {studentsData?.HallTicketSettings?.SHOW_HEADER_SCHOOL_ADDRESS && (
                          <p
                            className={`rcSubHeading font-bold ${schoolNameAlignment === `style2` && `text-center`}`}
                            style={{
                              fontSize: `${rangeValues.subHeading + 13}px`,
                              wordWrap: 'break-word',
                              overflowWrap: 'break-word',
                            }}
                          >
                            {authData?.BRANCH_ADDRESS?.toUpperCase()}
                          </p>
                        )}
                      </div>

                      {schoolNameAlignment === 'style1' && (
                        <div className="flex-shrink-0" style={{ width: `${rangeValues.logo * 12}px` }}></div>
                      )}

                    </div>)

                  }
                  <hr />
                  <div className='py-2'>
                    <div className={`flex mt-1 ml-9 ${examNameAlignment === 'center' ? `justify-center` : 'justify-start'}`}>
                      <h1 className="font-bold text-black"
                        style={{ fontSize: `${(rangeValues?.subHeading * 1.5) + 13}px` }}
                      // style={{
                      //       fontSize: (parseInt(
                      //         studentsData?.HallTicketSettings?.SCHOOL_NAME_FONT_SIZE,
                      //       ) - 3)
                      //     }}
                      >{studentsData?.Exam} Hall Ticket</h1>
                    </div>
                  </div>
                  {/* ---> STUDENT AI INSIGHT MOVED HERE <--- */}
                  {object?.STUDENT_AI_INSIGHT && (
                    <div className="w-auto mx-9 mt-4 mb-2 font-inter">
                      <div className="w-full bg-[#E9E9E94D] border border-[#A5A8AB] rounded-[8px] p-3 text-left">
                        {/* Header with Icon */}
                        <div className="flex items-center gap-2.5 mb-2">
                          <Info className="text-[#263238] transform rotate-180" size={22} strokeWidth={2} />
                          <h3 
                            className="font-[400] text-[#263238]" 
                            style={{ fontSize: `${(rangeValues?.text * 1.5) + 12}px` }}
                          >
                            Message
                          </h3>
                        </div>

                        {/* Text Content */}
                        <div 
                          className="text-[#555555] font-[400]   ml-8 text-justify leading-[1.3]"
                          style={{ fontSize: `${(rangeValues?.text * 1.5) + 10}px` }}
                        >
                          {object.STUDENT_AI_INSIGHT
                            .replace(/\\n|\n/g, ' ') 
                            .split('.')
                            .filter((sentence) => sentence.trim() !== '')
                            .map((sentence, index) => (
                              <div key={index}>
                                {sentence.trim()}.
                              </div>
                          ))}
                        </div>
                      </div>
                    </div>
                  )}
                  <div className={styles.hallticketBody}>
                    {studentsData?.HallTicketSettings?.SHOW_STUDENT && (
                      <div className="w-full flex justify-center items-center py-4 px-[2%] gap-4">
                        <div className="w-full flex items-center justify-around gap-4" style={{ fontSize: `${rangeValues?.text + 8}px` }}>
                          <div className="flex flex-col">
                            <div className='flex items-center gap-3'>
                              {studentsData?.HallTicketSettings?.SHOW_STUDENT_PHOTO && (
                                <div className="border border-black w-20 h-20 rounded-full overflow-hidden">
                                  <img
                                    src={object?.PhotoUrl || Avatar}
                                    alt="student-profile"
                                    className="w-full h-full object-cover"

                                  />
                                </div>
                              )}
                              <div className='flex flex-col'>
                                <span className="font-bold">{object.StudentName}</span>
                                <span className="text-[#0000004D]">Roll No:{object?.RollNo || ''}</span>
                              </div>
                            </div>
                          </div>
                          <div className="flex flex-col">
                            <span className='font-bold'>{studentsData?.Class || ''}</span>
                            <span className="text-[#0000004D]">Class</span>
                          </div>
                          {studentsData?.HallTicketSettings?.SHOW_STUDENT_ADMISSION_NUMBER && <div className="flex flex-col">
                            <span className='font-bold '>{object?.AdmissionNo || "-"}</span>
                            <span className="text-[#0000004D]">Admission No</span>
                          </div>}
                          <div className="flex flex-col">
                            <span className="font-bold">{object?.FatherName || "-"}</span>
                            <span className="font-normal text-[#0000004D]">Father Name</span>
                          </div>
                          {studentsData?.HallTicketSettings?.SHOW_MOBILE_NUMBER && (
                            <div className="flex flex-col">
                              <span className="font-bold">{object?.ParentMobileNo || '-'}</span>
                              <span className="font-normal text-[#0000004D]">Mobile No</span>
                            </div>
                          )}
                        </div>
                      </div>
                    )}

                    <div className={`w-[94%] mr-auto`}>
                      {!!(studentsData?.HallTicketSettings?.SHOW_EXAM_SUBJECT || studentsData?.HallTicketSettings?.SHOW_COSCHOLASTIC_SUBJECT || studentsData?.HallTicketSettings?.SHOW_ADDITIONAL_SUBJECT) ? (
                        <table className={`w-full mt-5  ${styles.htTable}`} style={{ fontSize: `${(rangeValues.text * 1.5) + 8}px` }} >
                          <thead className=''>
                            <tr className=''>
                              <th className="py-2">Subject Name</th>
                              <th className="p-2">Exam Date</th>
                              {studentsData?.HallTicketSettings?.SHOW_EXAM_SUBJECT_START_TIME && (
                                <th className="p-2 ">Start Time</th>
                              )}
                              {studentsData?.HallTicketSettings?.SHOW_EXAM_SUBJECT_END_TIME && (
                                <th className="p-2 ">End Time</th>
                              )}
                              {studentsData?.HallTicketSettings?.SHOW_EXAM_SUBJECT_STAFF_SIGNATURE && (
                                <th className="p-2 ">Invigilator Signature</th>
                              )}

                            </tr>
                          </thead>
                          <tbody>
                            {sortedSubjects?.map((subject, subjectIndex) => {
                              const examStartDate = moment(subject.ExamStartDatetime);
                              const examEndDate = moment(subject.ExamEndDatetime);
                              const startTime = `${examStartDate.format('hh:mm A')}`;
                              const endTime = `${examEndDate.format('hh:mm A')}`;

                              const showGeneral = studentsData?.HallTicketSettings?.SHOW_EXAM_SUBJECT;
                              const showCoscholastic = studentsData?.HallTicketSettings?.SHOW_COSCHOLASTIC_SUBJECT;
                              const showAdditional = studentsData?.HallTicketSettings?.SHOW_ADDITIONAL_SUBJECT;

                              return (
                                <tr key={`${objectIndex}-${subjectIndex}`}>
                                  {showGeneral && ['Regular', 'General']?.includes(subject.SubjectType) && (
                                    <td className="font-bold py-2" >{subject.SubjectName}</td>
                                  )}
                                  {showCoscholastic && subject.SubjectType == "Coscholastic" && (
                                    <td className="p-2 font-bold">{subject.SubjectName}{(subject?.SubjectType !== "General" && hasSubjectType) ? ` - (${subject?.SubjectType})` : ''}</td>
                                  )}
                                  {showAdditional && subject.SubjectType == 'Additional' && (
                                    <td className="p-2 font-bold">{subject.SubjectName}{(subject?.SubjectType !== "General" && hasSubjectType) ? ` - (${subject?.SubjectType})` : ''}</td>
                                  )}
                                  {showAdditional && subject.SubjectType == 'Optional' && (
                                    <td className="p-2 font-bold">{subject.SubjectName}{(subject?.SubjectType !== "General" && hasSubjectType) ? ` - (${subject?.SubjectType})` : ''}</td>
                                  )}
                                  <td className="p-2 font-bold">{examStartDate.format('DD-MM-YYYY')}</td>
                                  {studentsData?.HallTicketSettings?.SHOW_EXAM_SUBJECT_START_TIME && (
                                    <td className="p-2 font-bold">{startTime}</td>
                                  )}
                                  {studentsData?.HallTicketSettings?.SHOW_EXAM_SUBJECT_END_TIME && (
                                    <td className="p-2 font-bold">{endTime}</td>
                                  )}

                                  {/* {studentsData?.HallTicketSettings?.SHOW_EXAM_SUBJECT_STAFF_SIGNATURE && (
                                  <td className="p-2 font-bold border-b border-gray-300 w-40">
                                  </td>
                                )}
                                 */}

                                  {studentsData?.HallTicketSettings?.SHOW_EXAM_SUBJECT_STAFF_SIGNATURE && (
                                    <td className="pt-4">
                                      <div className="border-b w-full border-gray-600 mx-auto"></div>
                                    </td>
                                  )}
                                </tr>
                              );
                            })}
                          </tbody>
                        </table>
                      ) : null}

                    </div>

                    {studentsData?.HallTicketSettings?.SHOW_SIGNATURE && (
                      <div className='border-b border-black w-full mt-7'></div>
                    )}

                    {studentsData?.HallTicketSettings?.SHOW_SIGNATURE && (
  <div className="flex justify-between items-center mt-8 mb-2 px-6" style={{ fontSize: `${(rangeValues?.text * 1.5) + 8}px` }} >
    
    {/* 1. Class Teacher Signature */}
    {studentsData?.HallTicketSettings?.SHOW_SIGNATURE_CLASS_TEACHER && (
      <div className='text-center'>
        <div className='max-w-[7rem] h-[3rem]'>
          {/* <img src={authData?.PRINCIPAL_SIGN} alt="principal-signature" className='h-auto w-full' /> */}
        </div>
        <div className='border-t mb-1 min-w-[7rem] border-black font-semibold'></div>
        <span className='font-semibold'>Class Teacher</span>
      </div>
    )}

    {/* 2. Parent Signature */}
    {studentsData?.HallTicketSettings?.SHOW_SIGNATURE_PARENT && (
      <div className='text-center'>
        <div className='max-w-[7rem] h-[3rem]'>
          {/* <img src={authData?.PRINCIPAL_SIGN} alt="principal-signature" className='h-auto w-full' /> */}
        </div>
        <div className='border-t mb-1 min-w-[7rem] border-black font-semibold'></div>
        <span className='font-semibold'>Parent</span>
      </div>
    )}

   

    {/* 3. Principal Signature */}
    {studentsData?.HallTicketSettings?.SHOW_SIGNATURE_PRINCIPAL && (
      <div className='text-center'>
        <div className='max-w-[7rem] h-[3rem]'>
          {authData?.PRINCIPAL_SIGN && (
            <img src={authData?.PRINCIPAL_SIGN} className="h-auto w-full object-cover" />
          )}
        </div>
        <div className='border-t mb-1 min-w-[7rem] border-black font-semibold'></div>
        <span className='font-semibold'>Principal</span>
      </div>
    )}
  </div>
)}

                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

      </div>
    </div>
  ) :
    <SetupLoader />
});

